import { useNavigate } from 'react-router-dom-latest';

import { PageTabs, PageTabsBar } from '@liscio/ui';

export interface HeaderTab {
  /** The tab's text; a trailing record count ("Open | 12") becomes its count badge. */
  label: string;
  /** The route the tab opens, as HeaderTabs' tab links did. */
  url?: string;
}

interface PageHeaderTabsProps<T extends HeaderTab> {
  data: T[];
  activeTab: number;
  handleClick: (index: number, tab: T) => void;
  'aria-label': string;
  /** The id of the page's tab panel (the content the tabs switch), which each tab controls. */
  panelId?: string;
  /**
   * The URL that opens a tab in a new browser tab, for tabs that navigate in `handleClick`
   * (a tab with a `url` uses it).
   */
  tabHref?: (tab: T, index: number) => string;
}

/** The id of a tab, for its tab panel's `aria-labelledby`. */
export const pageHeaderTabId = (panelId: string, index: number) =>
  `${panelId}-tab-${index}`;

const COUNTED_LABEL = /^(.*?)\s*\|\s*(\d+)$/;

/**
 * A page's HeaderTabs data shown as the firm page tabs of the Files and Smart Tax pages, in their
 * full-width tab row.
 */
export const PageHeaderTabs = <T extends HeaderTab>({
  data,
  activeTab,
  handleClick,
  'aria-label': ariaLabel,
  panelId,
  tabHref,
}: PageHeaderTabsProps<T>) => {
  const navigate = useNavigate();

  const handleChange = (value: string) => {
    const index = Number(value);
    const tab = data[index];
    handleClick(index, tab);
    if (tab.url) navigate(tab.url);
  };

  return (
    <PageTabsBar>
      <PageTabs
        aria-label={ariaLabel}
        value={data[activeTab] ? String(activeTab) : false}
        onChange={handleChange}
        options={data.map((tab, index) => {
          const [, label, count] = tab.label.match(COUNTED_LABEL) ?? [];
          return {
            value: String(index),
            label: label ?? tab.label,
            count: count === undefined ? null : Number(count),
            id: panelId ? pageHeaderTabId(panelId, index) : undefined,
            'aria-controls': panelId,
            href: tab.url ?? tabHref?.(tab, index),
          };
        })}
      />
    </PageTabsBar>
  );
};

export default PageHeaderTabs;
